/**
 * StatisticsDisplay - 統計情報表示コンポーネント
 *
 * ColumnStatisticsとStatisticsSummaryElementRendererの両方から使用される
 * 共通の統計表示UIコンポーネント。
 *
 * 責務:
 * - ColumnStats（統計情報）を受け取り、グリッド形式で表示
 * - データ型別（数値/文字列/ブール/日付）の適切な表示
 * - カテゴリクリック機能のサポート（オプション）
 */

import { MouseEvent } from 'react';
import { ColumnStats } from '../../utils/StatisticsCalculator';
import * as styles from '../../styles/components/statistics/column-statistics.css';

export interface StatisticsDisplayProps {
  /** 表示する統計情報 */
  stats: ColumnStats;
  /** カテゴリクリック時のコールバック（オプション） */
  onCategoryClick?: (indices: number[], event: MouseEvent<HTMLSpanElement>) => void;
}

/**
 * 統計情報を表示するコンポーネント
 */
export function StatisticsDisplay({ stats, onCategoryClick }: StatisticsDisplayProps) {
  return (
    <div className={styles.statsGrid}>
      {/* 基本情報 */}
      <div className={styles.statItem}>
        <span className={styles.statLabel}>Data type:</span>
        <span className={styles.statValue}>{stats.type}</span>
      </div>
      <div className={styles.statItem}>
        <span className={styles.statLabel}>Valid values:</span>
        <span className={styles.statValue}>{stats.count.toLocaleString()}</span>
      </div>
      <div className={styles.statItem}>
        <span className={styles.statLabel}>Missing values:</span>
        <span className={styles.statValue}>{stats.nullCount.toLocaleString()}</span>
      </div>

      {/* 数値統計（尺度に応じて表示） */}
      {(stats.type === 'int64' || stats.type === 'float64') && (
        <>
          {/* Mode - すべての尺度で表示 */}
          {'mode' in stats && stats.mode && (
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Mode:</span>
              <span className={styles.statValue}>{stats.mode}</span>
            </div>
          )}

          {/* Min/Max/Median - ordinal以上の尺度で表示 */}
          {'min' in stats && stats.min && (
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Min:</span>
              <span className={styles.statValue}>{stats.min}</span>
            </div>
          )}
          {'max' in stats && stats.max && (
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Max:</span>
              <span className={styles.statValue}>{stats.max}</span>
            </div>
          )}
          {'median' in stats && stats.median && (
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Median:</span>
              <span className={styles.statValue}>{stats.median}</span>
            </div>
          )}

          {/* Mean/StdDev - interval以上の尺度で表示 */}
          {'mean' in stats && stats.mean && (
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Mean:</span>
              <span className={styles.statValue}>{stats.mean}</span>
            </div>
          )}
          {'std' in stats && stats.std && (
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Std Dev:</span>
              <span className={styles.statValue}>{stats.std}</span>
            </div>
          )}

          {/* CV/Geometric Mean - ratio尺度のみ */}
          {'cv' in stats && stats.cv && (
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Coef. of Variation:</span>
              <span className={styles.statValue}>{stats.cv}</span>
            </div>
          )}
          {'geometricMean' in stats && stats.geometricMean && (
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Geometric Mean:</span>
              <span className={styles.statValue}>{stats.geometricMean}</span>
            </div>
          )}

          {/* 尺度情報の表示 */}
          {stats.scale && (
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Scale:</span>
              <span className={styles.statValue}>{stats.scale}</span>
            </div>
          )}

          {/* 上位頻出値（nominal/ordinal尺度の場合、最大10個） */}
          {'categoryBreakdown' in stats && stats.categoryBreakdown && stats.categoryBreakdown.length > 0 && (
            <div className={styles.categoryBreakdown}>
              <span className={styles.categoryBreakdownLabel}>
                Most frequent (top 10):
              </span>
              <div className={styles.categoryList}>
                {stats.categoryBreakdown.map((category, index) => (
                  <div key={index} className={styles.statItem}>
                    <span className={styles.statLabel}>{category.value}:</span>
                    <span
                      className={onCategoryClick && category.indices ? styles.statValueClickable : styles.statValue}
                      onClick={onCategoryClick && category.indices ? (e) => onCategoryClick(category.indices, e) : undefined}
                      title={onCategoryClick && category.indices ? "Click to select rows with this value" : undefined}
                    >
                      {category.count.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* 文字列統計 */}
      {stats.type === 'string' && 'uniqueCount' in stats && (
        <>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>Unique values:</span>
            <span className={styles.statValue}>{stats.uniqueCount.toLocaleString()}</span>
          </div>
          {/* 上位頻出値（最大10個） */}
          {stats.categoryBreakdown && stats.categoryBreakdown.length > 0 && (
            <div className={styles.categoryBreakdown}>
              <span className={styles.categoryBreakdownLabel}>
                Most frequent {stats.uniqueCount > 10 ? '(top 10)' : ''}:
              </span>
              <div className={styles.categoryList}>
                {stats.categoryBreakdown.map((category, index) => (
                  <div key={index} className={styles.statItem}>
                    <span className={styles.statLabel}>{category.value}:</span>
                    <span
                      className={onCategoryClick && category.indices ? styles.statValueClickable : styles.statValue}
                      onClick={onCategoryClick && category.indices ? (e) => onCategoryClick(category.indices, e) : undefined}
                      title={onCategoryClick && category.indices ? "Click to select rows with this value" : undefined}
                    >
                      {category.count.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ブール統計 */}
      {stats.type === 'boolean' && 'trueCount' in stats && (
        <>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>True:</span>
            <span className={styles.statValue}>{stats.trueCount} ({stats.trueRatio})</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>False:</span>
            <span className={styles.statValue}>{stats.falseCount} ({stats.falseRatio})</span>
          </div>
        </>
      )}

      {/* Datetime/Date統計 */}
      {(stats.type === 'datetime' || stats.type === 'date') && (
        <>
          {'min' in stats && stats.min && (
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Earliest:</span>
              <span className={styles.statValue}>{stats.min}</span>
            </div>
          )}
          {'max' in stats && stats.max && (
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Latest:</span>
              <span className={styles.statValue}>{stats.max}</span>
            </div>
          )}
          {'range' in stats && stats.range && (
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Time span:</span>
              <span className={styles.statValue}>{stats.range}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
